import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import {
  ArrowLeftFromLine,
  BoxIcon,
  Building2Icon,
  Globe,
  LogOutIcon,
  MenuIcon,
  PlusIcon,
  UserIcon,
} from 'lucide-react';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Logo } from '@/components/ui/logo';
import { currentTenantAtom } from '@/constants/currrentTenant';
import { useUpdateLanguage } from '@/features/account/api/update-language';
import { useLogOut } from '@/features/auth/api/logout';
import { countSelectableTenants } from '@/features/tenants/utils/selectable-tenants';
import { findCurrentTenant } from '@/features/tenants/utils/find-tenant-with-parent';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';
import { getAvatarFallback } from '@/utils/avatar';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { GlobalSearchInput } from './global-search-input';
import { CreateEstateModal } from '../modals/create-estate-modal';
import { useModal } from '../modals/new/use-modal';
import { useTenantBranding } from '../providers/tenant-branding-provider';
import { UploadFloorPlanModal } from '../modals/upload-floor-plan-modal';

type HeaderProps = {
  onClick: Dispatch<SetStateAction<boolean>>;
  isVisible: boolean;
};

type TenantType = {
  id: string;
  name: string;
  address: {
    address: string | null;
    city: string | null;
    postalCode: string | null;
  } | null;
  subTenants: {
    id: string;
    name: string;
    parentTenantId: string | null;
  }[];
};

export const Header = ({ onClick, isVisible }: HeaderProps) => {
  const [isGlobalAddOpen, setIsGlobalAddOpen] = useState(false);
  const { logout } = useLogOut();
  const modal = useModal();
  const uploadFloorPlanModal = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const isUnderTenantPath = /^\/tenant\/[^/]+\/.+/.test(location.pathname);
  const { logoUrl } = useTenantBranding();
  const handleLogOut = async () => {
    await logout();
    // this seems to be the most reliable way to start the user journey from scratch
    window.location.reload();
  };

  const onClickHandler = () => onClick((prev) => !prev);

  const onAddEstateButtonClick = () => {
    setIsGlobalAddOpen(false);
    modal.open();
  };

  const onUploadFloorPlanClick = () => {
    setIsGlobalAddOpen(false);
    uploadFloorPlanModal.open();
  };

  const updateLanguage = useUpdateLanguage();
  const updateLanguageDebounced = useDebouncedCallback(updateLanguage.mutate);

  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    updateLanguageDebounced({ language: lang });
  };

  const { data } = useMyTenants();

  const [currentTenant, setCurrentTenant] = useAtom(currentTenantAtom);
  const selectedTenant = useMemo(
    () => findCurrentTenant(data, currentTenant?.id),
    [data, currentTenant?.id]
  );
  const me = useMe({
    tenantId: selectedTenant?.id,
  });
  const showAllTenantsOption = countSelectableTenants(data) > 0;

  const updateCurrentTenant = (tenant?: TenantType | TenantType['subTenants'][number]) => {
    setCurrentTenant(
      tenant
        ? {
            id: tenant.id,
            name: tenant.name,
          }
        : undefined
    );
    toast.success('Updated the current tenant');
  };

  const selectTenant = (tenant?: TenantType | TenantType['subTenants'][number]) => {
    if (isUnderTenantPath) {
      if (tenant) {
        navigate({
          to: '/tenant/$tenantId/users',
          params: { tenantId: tenant.id },
        });
      } else {
        navigate({ to: '/estates' });
      }
    }

    updateCurrentTenant(tenant);
  };

  return (
    <>
      <header className="flex h-[60px] items-center justify-between rounded-sm border-b border-gray-200 bg-primary-lighter px-4 lg:pl-10 lg:pr-14">
        <Button className="border-0 lg:hidden" variant="outline" size="sm" onClick={onClickHandler}>
          {isVisible ? <ArrowLeftFromLine /> : <MenuIcon />}
        </Button>
        <Logo className="!h-8 sm:block sm:h-auto" src={logoUrl ?? undefined} />
        <div className="ml-2 hidden w-full max-w-screen-sm items-center gap-2 sm:flex">
          <GlobalSearchInput />
          <DropdownMenu open={isGlobalAddOpen} onOpenChange={setIsGlobalAddOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                className="size-10 rounded-sm bg-primary-light text-white hover:bg-primary"
                variant="ghost"
              >
                <PlusIcon className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[21rem] p-0">
              <div className="border-b px-6 py-3 text-sm font-semibold text-primary-dark">
                {t('createNew')}
              </div>
              <DropdownMenuItem
                className="group cursor-pointer gap-6 rounded-none border-b px-6 py-3 focus:bg-primary-light focus:text-white"
                onSelect={onAddEstateButtonClick}
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-primary-light/10 text-primary-light group-focus:bg-white/10 group-focus:text-white">
                  <Building2Icon className="size-5" />
                </span>

                <span className="flex min-w-0 flex-col">
                  <span className="text-m font-semibold text-primary-dark group-focus:text-white">
                    {t('createEstateProject')}
                  </span>
                  <span className="text-sm text-slate-500 group-focus:text-slate-500">
                    {t('addNewPropertyOrProject')}
                  </span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="group cursor-pointer gap-6 rounded-none px-6 py-3 focus:bg-primary-light focus:text-white"
                onSelect={onUploadFloorPlanClick}
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-primary-light/10 text-primary-light group-focus:bg-white/10 group-focus:text-white">
                  <BoxIcon className="size-5" />
                </span>

                <span className="flex min-w-0 flex-col">
                  <span className="text-m font-semibold text-primary-dark group-focus:text-white">
                    {t('uploadFloorPlan')}
                  </span>
                  <span className="text-sm text-slate-500 group-focus:text-slate-500">
                    {t('quicklyAddFloorPlanToAnyEstate')}
                  </span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="items-center gap-[21px] sm:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="size-10 cursor-pointer sm:size-12">
                <AvatarImage
                  src={`/images/${me.data.images?.mediaId}?size=thumbnail100`}
                  alt="avatar"
                />
                <AvatarFallback>{getAvatarFallback(me.data)}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem asChild>
                <Link to="/profile">
                  <UserIcon />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="group">
                  <div className="flex items-center gap-4">
                    <Building2Icon className="flex-shrink-0" />
                    <div className="flex flex-col leading-5">
                      {t('tenant')}
                      <span className="text-xs text-neutral-500 group-hover:text-white">
                        {selectedTenant?.name ?? t('allTenants', 'All Tenants')}
                      </span>
                    </div>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {showAllTenantsOption ? (
                    <>
                      <DropdownMenuItem
                        className={cn(
                          'cursor-pointer px-6 py-3 leading-tight',
                          !selectedTenant ? 'bg-primary text-white' : 'bg-gray-100'
                        )}
                        onSelect={() => selectTenant()}
                      >
                        {t('allTenants', 'All Tenants')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {data.map((tenant, index: number) => {
                    const isTenantSelected = selectedTenant?.id === tenant.id;

                    return (
                      <div key={tenant.id}>
                        <DropdownMenuItem
                          className={cn(
                            'cursor-pointer px-6 py-3 leading-tight',
                            isTenantSelected ? 'bg-primary text-white' : 'bg-gray-100'
                          )}
                          onSelect={() => selectTenant(tenant)}
                        >
                          {tenant.name}
                        </DropdownMenuItem>
                        {tenant.subTenants?.map((subTenant) => {
                          return (
                            <DropdownMenuItem
                              key={subTenant.name}
                              className={cn(
                                'my-2 ml-4 cursor-pointer',
                                selectedTenant?.id === subTenant.id
                                  ? 'bg-primary text-white'
                                  : 'bg-gray-100 hover:bg-primary hover:text-white'
                              )}
                              onSelect={() => selectTenant(subTenant)}
                            >
                              {subTenant.name}
                            </DropdownMenuItem>
                          );
                        })}
                        {index !== data.length - 1 ? <DropdownMenuSeparator /> : null}
                      </div>
                    );
                  })}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <div className="flex items-center gap-4">
                    <Globe />
                    <div className="flex flex-col leading-5">
                      {t('language')}
                      <span className="uppercase">{currentLang}</span>
                    </div>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    className={cn(
                      currentLang === 'en'
                        ? 'pointer-events-none cursor-not-allowed text-neutral-400'
                        : 'cursor-pointer'
                    )}
                    disabled={currentLang === 'en'}
                    onClick={() => currentLang !== 'en' && changeLanguage('en')}
                  >
                    {t('english')}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    className={cn(
                      currentLang === 'de'
                        ? 'pointer-events-none cursor-not-allowed text-neutral-400'
                        : 'cursor-pointer'
                    )}
                    disabled={currentLang === 'de'}
                    onClick={() => currentLang !== 'de' && changeLanguage('de')}
                  >
                    {t('german')}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <button type="button" className="w-full text-left" onClick={handleLogOut}>
                  <LogOutIcon />
                  Logout
                </button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <CreateEstateModal navigate={navigate} modal={modal} />
      <UploadFloorPlanModal modal={uploadFloorPlanModal} />
    </>
  );
};
